/**
 * Typed client for the six user-facing MFA endpoints.
 *
 * Self-service: enroll, verifyEnrollment, status, regenerateRecoveryCodes,
 * disable. Org-admin: resetForUser (only members of the caller's
 * organization — backend enforces). The system-admin reset endpoint
 * lives in admin-mfa.ts (AdminMFAAPI).
 */
import type { components } from '../../types/api.generated';
import { userApiClient } from './client';

export type EnrollMFAResponse = components['schemas']['EnrollMFAResponse'];
export type VerifyEnrollmentResponse = components['schemas']['VerifyEnrollmentResponse'];
export type MFAStatusResponse = components['schemas']['MFAStatusResponse'];
export type RegenerateRecoveryCodesResponse =
  components['schemas']['RegenerateRecoveryCodesResponse'];

export const MFAAPI = {
  async enroll(): Promise<EnrollMFAResponse> {
    return userApiClient.post<EnrollMFAResponse>('/api/v1/auth/mfa/enroll');
  },

  async verifyEnrollment(factorId: string, code: string): Promise<VerifyEnrollmentResponse> {
    return userApiClient.post<VerifyEnrollmentResponse>('/api/v1/auth/mfa/verify-enrollment', {
      factorId,
      code,
    });
  },

  async status(signal?: AbortSignal): Promise<MFAStatusResponse> {
    return userApiClient.get<MFAStatusResponse>('/api/v1/auth/mfa/status', { signal });
  },

  async regenerateRecoveryCodes(currentCode: string): Promise<RegenerateRecoveryCodesResponse> {
    return userApiClient.post<RegenerateRecoveryCodesResponse>(
      '/api/v1/auth/mfa/regenerate-recovery-codes',
      { currentCode },
    );
  },

  async disable(currentCode: string): Promise<void> {
    await userApiClient.post('/api/v1/auth/mfa/disable', { currentCode });
  },

  async resetForUser(userId: string): Promise<void> {
    await userApiClient.post(`/api/v1/users/${userId}/mfa/reset`);
  },
};
